import React, {useEffect, useState} from 'react';
import {IonButton, IonDatetime, IonIcon, IonInput, IonItem, IonModal, IonTitle} from "@ionic/react";
import {InputChangeEventDetail} from "@ionic/core";
import {IonDatetimeCustomEvent, IonInputCustomEvent} from "@ionic/core/dist/types/components";
import {format, parseISO} from "date-fns";
import {handleNumericOnlyPress} from "../utils";
import {calendarOutline} from "ionicons/icons";
import './DateInput.css';
import {DatetimeChangeEventDetail} from "@ionic/core/dist/types/components/datetime/datetime-interface";

interface DateInputProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    title?: string;
}

const DateInput: React.FC<DateInputProps> = ({
                                                 value: propValue = '',
                                                 onChange,
                                                 placeholder = 'dd/MM/yyyy',
                                                 className = '',
                                                 title = ''
                                             }) => {
    const [inputValue, setInputValue] = useState(propValue);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setInputValue(propValue);
    }, [propValue]);

    const formatDate = (input: any): string => {
        // Remove all non-digit characters
        const digits = input.replace(/\D/g, '');

        // Add slashes after day and month
        let formatted = '';
        for (let i = 0; i < digits.length && i < 8; i++) {
            if (i === 2 || i === 4) {
                formatted += '/';
            }
            formatted += digits[i];
        }

        return formatted;
    };

    const validateDateFormat = (dateStr: string): boolean => {
        if (dateStr.length !== 10) return false;

        const parts = dateStr.split('/');
        if (parts.length !== 3) return false;

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
        if (month < 1 || month > 12) return false;
        if (day < 1 || day > 31) return false;
        if (year < 1000 || year > 9999) return false;

        // Check days in month
        const daysInMonth = new Date(year, month, 0).getDate();
        if (day > daysInMonth) return false;

        return true;
    };

    const handleChange = (e: IonInputCustomEvent<InputChangeEventDetail>) => {
        const newValue = formatDate(e.detail.value);
        setInputValue(newValue);

        if (onChange) {
            onChange(newValue);
        }
    };

    const handleBlur = () => {
        if (inputValue.length === 10 && !validateDateFormat(inputValue)) {
            setInputValue('');
            if (onChange) {
                onChange('');
            }
        }
    };

    return (
        <IonItem>
            <IonInput
                type="text"
                value={inputValue}
                onKeyPress={handleNumericOnlyPress}
                onIonChange={handleChange}
                onIonBlur={handleBlur}
                placeholder={placeholder}
                className={`px-3 py-2 border rounded-md ${className}`}
                maxlength={10}
                pattern="\d{2}/\d{2}/\d{4}"
            />

            <IonButton
                slot="end"
                size="small"
                color="primary"
                onClick={() => setIsOpen(true)}
            >
                <IonIcon slot="icon-only" icon={calendarOutline}></IonIcon>
            </IonButton>
            <IonModal isOpen={isOpen} keepContentsMounted
                      onDidDismiss={() => setIsOpen(false)}
                      className="calendar-modal"
            >
                <IonItem><IonTitle>{title}</IonTitle></IonItem>
                <IonDatetime
                    presentation="date"
                    onIonChange={(event: IonDatetimeCustomEvent<DatetimeChangeEventDetail>) => {
                        const value :string = Array.isArray(event.detail.value!) ? event.detail.value![0] : event.detail.value!;
                        setInputValue(format(parseISO(value), 'dd/MM/yyyy'));
                        setIsOpen(false);
                    }}
                >
                </IonDatetime>
            </IonModal>

        </IonItem>
    );
};

export default DateInput;
